/**
 * Currency Manager for Marketing Pages
 * Minimal implementation to pass failing tests
 */

interface Currency {
  code: string;
  symbol: string;
  name: string;
  region: string;
}

export class CurrencyManager {
  private static readonly CURRENCIES: Currency[] = [
    { code: 'GBP', symbol: '£', name: 'British Pound', region: 'UK' },
    { code: 'EUR', symbol: '€', name: 'Euro', region: 'EU' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', region: 'Switzerland' }
  ];

  // Plan-based pricing per currency (same number across all currencies)
  private static readonly PLAN_PRICING: Record<'STARTER' | 'PRO' | 'PLUS', Record<string, number>> = {
    STARTER: {
      GBP: 149,
      EUR: 149,
      CHF: 149,
    },
    PRO: {
      GBP: 349,
      EUR: 349,
      CHF: 349,
    },
    PLUS: {
      GBP: 599,
      EUR: 599,
      CHF: 599,
    },
  };

  // Onboarding cost per currency
  private static readonly ONBOARDING_COST: Record<string, number> = {
    GBP: 690,
    EUR: 690,
    CHF: 690,
  };

  private static readonly ONBOARDING_STANDARD: Record<string, number> = {
    GBP: 299,
    EUR: 299,
    CHF: 299,
  };

  private static readonly ONBOARDING_COMPLIANCE: Record<string, number> = {
    GBP: 699,
    EUR: 699,
    CHF: 699,
  };

  private static readonly PILOT_LOW: Record<string, number> = {
    GBP: 699,
    EUR: 699,
    CHF: 699,
  };

  private static readonly PILOT_HIGH: Record<string, number> = {
    GBP: 1999,
    EUR: 1999,
    CHF: 1999,
  };

  getAvailableCurrencies(): Currency[] {
    return CurrencyManager.CURRENCIES;
  }

  /**
   * Backward-compatible: returns PRO plan price for a given currency
   */
  getPriceForCurrency(currency: string): number {
    return this.getPlanPrice('PRO', currency);
  }

  getPlanPrice(plan: 'STARTER' | 'PRO' | 'PLUS', currency: string): number {
    if (!this.isValidCurrency(currency)) {
      throw new Error(`Currency ${currency} not supported for this region`);
    }
    return CurrencyManager.PLAN_PRICING[plan][currency];
  }

  /**
   * Calculate annual price with two months free (10× monthly)
   * @param plan - Plan type
   * @param currency - Currency code
   * @returns Annual price
   */
  getAnnualPrice(plan: 'STARTER' | 'PRO' | 'PLUS', currency: string): number {
    const monthlyPrice = this.getPlanPrice(plan, currency);
    return monthlyPrice * 10;
  }

  /**
   * Get the monthly equivalent when paying annually
   * @param plan - Plan type
   * @param currency - Currency code
   * @returns Effective monthly cost when paying annually
   */
  getAnnualMonthlyEquivalent(plan: 'STARTER' | 'PRO' | 'PLUS', currency: string): number {
    const annualPrice = this.getAnnualPrice(plan, currency);
    return annualPrice / 12;
  }

  /**
   * Calculate savings when choosing annual vs monthly
   * @param plan - Plan type
   * @param currency - Currency code
   * @returns Amount saved per year
   */
  getAnnualSavings(plan: 'STARTER' | 'PRO' | 'PLUS', currency: string): number {
    const monthlyPrice = this.getPlanPrice(plan, currency);
    const annualPrice = this.getAnnualPrice(plan, currency);
    return (monthlyPrice * 12) - annualPrice;
  }

  /**
   * Get onboarding cost for a currency
   * @param currency - Currency code
   * @returns Onboarding cost
   */
  getOnboardingCost(currency: string): number {
    if (!this.isValidCurrency(currency)) {
      throw new Error(`Currency ${currency} not supported for this region`);
    }
    return CurrencyManager.ONBOARDING_COST[currency];
  }

  getOnboardingStandard(currency: string): number {
    if (!this.isValidCurrency(currency)) {
      throw new Error(`Currency ${currency} not supported for this region`);
    }
    return CurrencyManager.ONBOARDING_STANDARD[currency];
  }

  getOnboardingCompliance(currency: string): number {
    if (!this.isValidCurrency(currency)) {
      throw new Error(`Currency ${currency} not supported for this region`);
    }
    return CurrencyManager.ONBOARDING_COMPLIANCE[currency];
  }

  getPilotLow(currency: string): number {
    if (!this.isValidCurrency(currency)) {
      throw new Error(`Currency ${currency} not supported for this region`);
    }
    return CurrencyManager.PILOT_LOW[currency];
  }

  getPilotHigh(currency: string): number {
    if (!this.isValidCurrency(currency)) {
      throw new Error(`Currency ${currency} not supported for this region`);
    }
    return CurrencyManager.PILOT_HIGH[currency];
  }

  isValidCurrency(currency: string | null | undefined): boolean {
    if (!currency || typeof currency !== 'string') {
      return false;
    }
    return CurrencyManager.CURRENCIES.some(c => c.code === currency);
  }
}
